import clsx from 'clsx';
import {
  type FocusEvent,
  forwardRef,
  type MutableRefObject,
  type PropsWithChildren,
  type Ref,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
  type ReactElement,
  type SetStateAction,
  type Dispatch,
} from 'react';
import ReactSelect, { createFilter, type GroupBase } from 'react-select';

import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
  type AriaLabelingProps,
  type DOMProps,
} from '@dynatrace/strato-components/core';
import { _useAriaLabelingProps } from '@dynatrace/strato-components/core';

import { components } from './components/components.js';
import { SelectMenuContext } from './contexts/SelectMenuContext.js';
import { useSelectV2Options, _isOption } from './hooks/use-select-options.js';
import { useSelectV2Slots } from './hooks/use-select-slots.js';
import { useSelectV2Value } from './hooks/use-select-value.js';
import { SelectV2Content } from './SelectContent.js';
import { SelectV2CustomTrigger } from './SelectCustomTrigger.js';
import { SelectV2DisplayValue } from './SelectDisplayValue.js';
import {
  SelectV2EmptyState,
  type SelectV2EmptyStateProps,
} from './SelectEmptyState.js';
import { SelectV2Filter, type SelectV2FilterProps } from './SelectFilter.js';
import { SelectV2Group, SelectV2GroupLabel } from './SelectGroup.js';
import { SelectV2Option } from './SelectOption.js';
import { SelectV2Prefix, type SelectV2PrefixProps } from './SelectPrefix.js';
import { SelectV2Suffix } from './SelectSuffix.js';
import { SelectV2Trigger } from './SelectTrigger.js';
import {
  reducer,
  SelectActions,
  type SelectReducerAction,
} from './state/select-menu-state-reducer.js';
import {
  clearReactSelectZIndex,
  selectWrapperCSS,
  validAndEnabledCSS,
} from './styles/Select.sty.js';
import type {
  BaseOption,
  OptionOrGroup,
  SelectContentWidthOptionsType,
  SelectTriggerWidthOptionsType,
  SelectV2MultiValue,
  SelectV2SingleValue,
} from './types.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useFormControlState } from '../../core/hooks/useFormControlState.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import type {
  FormControlState,
  FormControlStateProps,
} from '../../core/types/form-control-state.js';
import { withFormFieldMessageContextProviderWithGenerics } from '../forms-core/components/form-field/FormFieldMessageContextProvider.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { useFormFieldValidation } from '../forms-core/hooks/useFormFieldValidation.js';
import { useSyncedFormControlProps } from '../forms-core/hooks/useSyncedFormControlProps.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import type {
  FormControlRef,
  FormControlWithOverlayRef,
} from '../shared-types.js';

// Redeclare react-select module to extend it with additional props for typesafety.
// https://react-select.com/typescript#custom-select-props
declare module 'react-select/dist/declarations/src/Select' {
  export interface Props<
    Option,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    IsMulti extends boolean,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Group extends GroupBase<Option>,
  > {
    /** Custom placeholder text */
    placeholderText?: ReactNode;
    /** The trigger width set by the dev. */
    triggerWidth?: SelectTriggerWidthOptionsType;
    /** Custom select trigger */
    customTrigger?: ReactElement<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { [key: string]: any } | null | undefined
    >;
    /** Custom display value. */
    displayValue?: ReactNode[];
    /** Prefix for the select trigger */
    prefix?: ReactElement<SelectV2PrefixProps>;
    /* TriggerRef that needs to be applied to the trigger in order to setup the menu positioning */
    triggerRef: HTMLElement | null;
    /* setter for the TriggerRef */
    setTriggerRef: Dispatch<SetStateAction<HTMLDivElement | null>>;
    /** Defines if the space for the prefix icons should be reserved in all options rendered */
    optionsHavePrefix: boolean;
    /** Select.Filter component specified by dev */
    customFilter?: ReactElement<SelectV2FilterProps>;
    /** Custom empty state provided by dev */
    emptyState?: ReactElement<SelectV2EmptyStateProps>;
    /** Form control state for errors and hints */
    controlState?: FormControlState;
    loading?: boolean;
    clearable?: boolean;
    isRequired?: boolean;
    /** array of all select options (including those in groups) */
    allOptions?: BaseOption<Option>[];
    hiddenSelectRef: Ref<HTMLSelectElement>;
    hiddenSelectName?: string;
    /** whether the focus styles on the options should be visible */
    focusOnOption: boolean;
    /** Ref that needs to be applied to the filter input to manage the focus and keyboard navigation */
    filterRef?: MutableRefObject<FormControlRef<HTMLDivElement> | null>;
    /** Ref that needs to be applied to the clear selection element to manage the focus and keyboard navigation */
    clearSelectionRef?: RefObject<HTMLElement | null>;
    /** Ref that needs to be applied to the select content (menulist) element to manage the focus and keyboard navigation */
    contentRef?: RefObject<HTMLDivElement | null>;
    menuListFunctionRef?: RefObject<MenuListFunctionRefObject | null>;
    /** The content width set by the dev. */
    contentWidth?: SelectContentWidthOptionsType;
    /** Whether the selected options are reordered to the top of the options. */
    showSelectedOptionsFirst?: boolean;
    /** The id used for the hidden input. */
    selectInputId?: string;
    /** All aria-labeling props. */
    ariaLabelingProps?: AriaLabelingProps;
    /** className and style prop for each of the components. */
    componentProps: {
      trigger?: StylingProps &
        DataTestId & { readOnly: boolean; ariaDisabled?: boolean };
      content?: StylingProps & DataTestId;
      filter?: StylingProps & DataTestId;
      emptyState?: StylingProps & DataTestId;
    };
  }
}

// Redeclare react forwardRef to allow generic types on react
// https://fettblog.eu/typescript-react-generic-forward-refs/#option-3%3A-augment-forwardref
declare module 'react' {
  function forwardRef<T, P = unknown>(
    render: (props: P, ref: React.Ref<T>) => React.ReactElement | null,
  ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

/**
 * Common select base props.
 * @public
 */
export type SelectV2BaseProps<T, Multiple extends boolean> = PropsWithChildren<{
  /**
   * Used to compare options with objects for values either by a specified property of the object or through a comparator function.
   */
  compareBy?: keyof T | ((valueA: T, valueB: T) => boolean);

  /** Name used for the component when submitting it in a form */
  name?: string;

  /** Form name used to connect the component to a form */
  form?: string;

  /**
   * Boolean to determine whether the component is required
   *
   * @defaultValue false
   */
  required?: boolean;

  /**
   * Whether the component is disabled
   *
   * @defaultValue false
   */
  disabled?: boolean;

  /**
   * Event handler called when the open state for the select changes
   */
  onOpenChange?: (isOpen: boolean) => void;

  /**
   * Whether or not the select allows multiple selection or not
   * @defaultValue false
   */
  multiple?: Multiple;

  /**
   * Whether or not the select is clearable.
   * @defaultValue false for single select, true for multi select
   */
  clearable?: boolean;

  /** Callback that is called when the select trigger loses focus. */
  onBlur?: (e: FocusEvent<HTMLInputElement, Element>) => void;

  /** Callback that is called when the select trigger gets focus. */
  onFocus?: (e: FocusEvent<HTMLInputElement, Element>) => void;
}> &
  MaskingProps;

/**
 * @public
 */
export type SelectV2OnChangeValue<
  OptionValue,
  Multiple extends boolean,
> = Multiple extends true
  ? SelectV2MultiValue<OptionValue>
  : SelectV2SingleValue<OptionValue>;

/**
 * Accepted properties for Select
 * @public
 */
export type SelectV2Props<
  /*
   * TODO: Figure out how we can properly infer the type of the Select here
   * because that would come from the value of the options (as children)
   * which is not really doable with the given API.
   */
  OptionValue,
  Multiple extends boolean,
> = SelectV2BaseProps<OptionValue, Multiple> &
  FormControlProps<
    Multiple extends true
      ? SelectV2MultiValue<OptionValue>
      : SelectV2SingleValue<OptionValue>,
    (value: SelectV2OnChangeValue<OptionValue, Multiple>) => void
  > &
  FormControlStateProps &
  DOMProps &
  StylingProps &
  DataTestId;

/**
 * @internal
 */
export interface MenuListFunctionRefObject {
  /** Function to scroll to the last option. */
  scrollToBottom: () => void;
}

/**
 * Function to return the item that should be focused after
 * updating all options via onChange in a controlled multi select.
 * @param options - all select options
 * @param focusedOptionValue - the value of the previously focused option
 * @returns option with the same value as the previously focused option or null
 */
function getNewFocusedOption(
  options: OptionOrGroup<BaseOption<unknown>>,
  focusedOptionValue: unknown,
): BaseOption<unknown> | null {
  for (const optionOrGroup of options) {
    if (_isOption(optionOrGroup)) {
      if (optionOrGroup.value === focusedOptionValue) {
        return optionOrGroup;
      }
    } else {
      for (const option of optionOrGroup.options) {
        if (_isOption(option) && option.value === focusedOptionValue) {
          return option;
        }
      }
    }
  }

  return null;
}

/** Select component */
function SelectV2Fwd<OptionValue, Multiple extends boolean = false>(
  props: SelectV2Props<OptionValue, Multiple>,
  forwardedRef: Ref<FormControlWithOverlayRef>,
) {
  const {
    multiple = false,
    disabled: propDisabled,
    controlState,
    clearable = multiple,
    name,
    id: propId,
    readOnly = false,
    onFocus,
    onBlur,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId = 'selectv2',
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-disabled': ariaDisabledProp,
    onOpenChange,
    required: propRequired,
    ...remainingProps
  } = props;

  const slots = useSelectV2Slots(props.children);

  const {
    optionsOrGroups: options,
    optionsHavePrefix,
    allOptions,
  } = useSelectV2Options<OptionValue>(slots.Content);

  const { value, defaultValue } = useSelectV2Value<OptionValue, Multiple>({
    value: props.value,
    defaultValue: props.defaultValue,
    options,
    multiple: (multiple === true) as Multiple,
  });

  const hiddenSelectRef = useRef<HTMLSelectElement>(null);
  const { onBlur: validationOnBlur } = useFormFieldValidation(hiddenSelectRef);

  const [triggerRef, setTriggerRef] = useState<HTMLDivElement | null>(null);

  const menuOpenReducer = useCallback(
    (state: boolean, action: SelectReducerAction) => {
      if (readOnly) {
        return state;
      }

      const newState = reducer(state, action);

      if (state !== newState) {
        // Delay the onOpenChange call to not run into a bad setState().
        queueMicrotask(() => {
          newState === false && !controlState && validationOnBlur();
          onOpenChange?.(newState);
        });
      }

      return newState;
    },
    [readOnly, controlState, validationOnBlur, onOpenChange],
  );

  const [menuOpen, dispatch] = useReducer(menuOpenReducer, false);

  const generatedUniqueId = useId();
  const {
    disabled,
    required,
    id: contextOrPropId,
    ariaDisabled,
  } = useSyncedFormControlProps({
    propDisabled,
    propRequired,
    propId,
    propAriaDisabled: ariaDisabledProp,
  });
  const id = contextOrPropId || generatedUniqueId;

  const [filterValue, setFilterValue] = useControllableState({
    value: slots.Filter?.props.value,
    defaultValue: slots.Filter?.props.defaultValue,
    onChange: slots.Filter?.props.onChange,
  });

  const { state: messagesContextState, dispatch: messagesContextDispatch } =
    useFormFieldMessagesContext();

  // any is needed as the needed type coming from the react-select is not exported
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectRef = useRef<any>(null);

  const hiddenTrigger = useMemo(
    () => triggerRef?.querySelector('input'),
    [triggerRef],
  );
  const [focusOnOption, setFocusOnOption] = useState<boolean>(true);

  const ariaLabelingProps = _useAriaLabelingProps(remainingProps);

  // If the disableFiltering value changes, the filterValue will need to be
  // reset
  useEffect(() => {
    if (
      slots.Filter?.props.disableFiltering === true &&
      (filterValue !== undefined || filterValue !== '')
    ) {
      setFilterValue('');
    }
    // We don't want to include filterValue as dependency because when the user types in the input and
    // disableFiltering is true, we don't want to clear the input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots.Filter?.props.disableFiltering]);

  const filterConfig = {
    // when a textValue is on the option, the textValue is used;
    // otherwise we try and stringify whatever possible.
    stringify: (option: {
      label: string;
      value: string;
      data: BaseOption<OptionValue>;
    }) => {
      return `${option.label}`;
    },
  };

  const handleChange = (
    option:
      | SelectV2SingleValue<BaseOption<OptionValue>>
      | SelectV2MultiValue<BaseOption<OptionValue>>,
  ) => {
    // Send native onChange event for any form control / onChange listeners
    requestAnimationFrame(() => {
      const nativeEvent = new Event('change', {
        bubbles: true,
        cancelable: true,
      });
      hiddenSelectRef?.current?.dispatchEvent(nativeEvent);
    });

    if (multiple === false) {
      setFilterValue(''); // clear the filter when an option is selected in single mode
    }

    // Early exit if the onChange prop is not defined, there is nothing that needs
    // to be done.
    if (!props.onChange) {
      return;
    }

    // In a single select scenario, it will only emit a single value of the selected option
    if (multiple === false) {
      // If the clicked option is the same as the selected one we can return early.
      if (option === value) {
        return;
      }
      if (option === null) {
        // Ensures that null is returned if no value is selected
        props.onChange?.(null as SelectV2OnChangeValue<OptionValue, Multiple>);
      } else {
        props.onChange?.(
          (option as SelectV2SingleValue<BaseOption<OptionValue>>)
            ?.value as SelectV2OnChangeValue<OptionValue, Multiple>,
        );
      }
    }

    // In a multi select scenario, it will map the value of all selected options and report them
    // in the on change.
    if (multiple === true) {
      const selectedOptionValues = (
        option as SelectV2MultiValue<BaseOption<OptionValue>>
      ).map((individualOption) => individualOption.value);
      props.onChange?.(
        selectedOptionValues as SelectV2OnChangeValue<OptionValue, Multiple>,
      );
    }
  };

  const filterRef = useRef<FormControlRef<HTMLDivElement>>(null);
  const clearSelectionRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const menuListFunctionRef = useRef<MenuListFunctionRefObject>(null);

  const handleArrowKeyOptionFocus = (key: KeyboardEvent['key']) => {
    if (key !== 'ArrowUp' && key !== 'ArrowDown') {
      return;
    }

    requestAnimationFrame(() => {
      if (!contentRef.current) {
        return;
      }

      const container = contentRef.current;
      const focusedElement = container.querySelector(
        '.strato-selectV2-option--focus',
      );

      if (focusedElement) {
        focusedElement.scrollIntoView({
          block: 'nearest',
        });
      } else if (container) {
        if (key === 'ArrowDown') {
          container.scrollTop = 0;
        } else if (menuListFunctionRef.current) {
          menuListFunctionRef.current.scrollToBottom();
        }
      }
    });
  };

  useImperativeHandle(
    forwardedRef,
    (): FormControlWithOverlayRef => ({
      element: containerRef.current,
      triggerRef: triggerRef,
      inputRef: hiddenSelectRef.current,
      focus: () => {
        hiddenTrigger?.focus();
      },
      open: () => {
        // When opened programmatically the hidden input needs to be focused for the virtual focus
        hiddenTrigger?.focus();
        dispatch({
          type: SelectActions.OPEN,
        });
      },
      close: () =>
        dispatch({
          type: SelectActions.CLOSE,
        }),
      validate: () => {
        // The initialization of the hiddenSelectRef is delayed, therefore,
        // we want to check the validity at the end of the render cycle.
        queueMicrotask(() => hiddenSelectRef.current?.reportValidity());
        messagesContextDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
      },
    }),
    [hiddenTrigger, messagesContextDispatch, triggerRef],
  );

  /**
   * Workaround for the focus to stay on the selected option for the controlled multi select
   * See: https://github.com/JedWatson/react-select/issues/5367
   */
  if (multiple && props.onChange) {
    const select = selectRef.current;
    if (select?.state.focusedOption) {
      const focusedOptionValue = select.state.focusedOption.value;
      const newFocusedOption = getNewFocusedOption(options, focusedOptionValue);

      // HACK: manipulating the state without setState is evil (but needed here as we are mid-transition)
      select.state.focusedOption = newFocusedOption;
    }
  }

  const [hasFormControlError] = useFormControlState(controlState);
  const showError = controlState
    ? hasFormControlError
    : messagesContextState.showError;

  const contextMemo = useMemo(
    () => ({ state: menuOpen, dispatch }),
    [menuOpen, dispatch],
  );

  const onBlurHandler = (event: FocusEvent<HTMLInputElement, Element>) => {
    if (
      event.relatedTarget !== hiddenTrigger &&
      event.relatedTarget !== clearSelectionRef.current &&
      event.relatedTarget !== filterRef.current?.inputRef
    ) {
      onBlur?.(event);
    }
  };

  return (
    <SelectMenuContext.Provider value={contextMemo}>
      <div
        ref={containerRef}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        className={clsx(
          selectWrapperCSS({
            fullWidth:
              slots.Trigger?.props.width === 'full' ||
              slots.Trigger?.props.width === '100%',
          }),
          consumerClassName,
        )}
        style={consumerStyle}
        onBlur={onBlurHandler}
      >
        <ReactSelect
          instanceId={id}
          selectInputId={id}
          className={clsx(clearReactSelectZIndex, {
            [validAndEnabledCSS]: !disabled && !ariaDisabled && !showError,
          })}
          unstyled
          options={options}
          components={components}
          // TODO: React select does an instance equality check on this one, so we need to figure out how
          // to keep that in sync over a controlled state.
          value={value}
          defaultValue={defaultValue}
          onChange={(value) =>
            // Need to cast this here as React.Select handles the value still as unknown.
            // TODO: Maybe we can go to the JSX generic?
            handleChange(
              value as
                | SelectV2SingleValue<BaseOption<OptionValue>>
                | SelectV2MultiValue<BaseOption<OptionValue>>,
            )
          }
          hiddenSelectName={name}
          ref={selectRef}
          pageSize={5}
          ariaLabelingProps={ariaLabelingProps}
          aria-disabled={ariaDisabled}
          hiddenSelectRef={hiddenSelectRef}
          // This has to be set to false as we don't want the input directly in the select trigger
          // but handle this ourselves
          isSearchable={false}
          // Multi selection relevant props
          isMulti={props.multiple ?? false}
          hideSelectedOptions={false}
          closeMenuOnSelect={multiple === false}
          // This has to be set to false as we don't want the clear button directly in the select trigger
          // but handle this ourselves
          isClearable={false}
          isDisabled={disabled}
          isRequired={required}
          triggerWidth={slots.Trigger?.props.width}
          contentWidth={slots.Content?.props.width ?? 'trigger'}
          placeholderText={slots.Trigger?.props.placeholder}
          // DISCUSS: Figure out if `undefined` is better here or if we should use `null`
          displayValue={slots.DisplayValue ?? undefined}
          prefix={slots.Prefix ?? undefined}
          customTrigger={slots.CustomTrigger ?? undefined}
          // Trigger and Menu (Popper) ref connections
          triggerRef={triggerRef}
          setTriggerRef={setTriggerRef}
          // Reserving space for prefix icons
          optionsHavePrefix={optionsHavePrefix}
          menuIsOpen={menuOpen}
          // Handles Filtering in the current select
          filterOption={(option, inputValue) => {
            if (slots.Filter?.props.disableFiltering === true) {
              return true;
            }
            const filterFunction =
              createFilter<BaseOption<OptionValue>>(filterConfig);

            // We need to cast this here, because the filter option prop only infers option as `FilterOptionOption<unknown>`. The `FilterOptionOption` type is not exposed by react-select.
            return filterFunction(
              option as Parameters<typeof filterFunction>[0],
              inputValue,
            );
          }}
          customFilter={slots.Filter ?? undefined}
          inputValue={filterValue}
          onInputChange={(newValue, actionmeta) => {
            if (actionmeta.action === 'input-change') {
              setFilterValue(newValue);
            }
          }}
          emptyState={slots.EmptyState ?? undefined}
          controlState={controlState}
          loading={slots.Content?.props.loading}
          clearable={clearable}
          allOptions={allOptions}
          tabSelectsValue={false}
          focusOnOption={focusOnOption}
          contentRef={contentRef}
          menuListFunctionRef={menuListFunctionRef}
          filterRef={filterRef}
          clearSelectionRef={clearSelectionRef}
          showSelectedOptionsFirst={
            slots.Content?.props.showSelectedOptionsFirst
          }
          componentProps={{
            trigger: {
              className: slots.Trigger?.props.className,
              // pointerEvents need to be enabled to change the cursor to not-allowed.
              style: { pointerEvents: 'auto', ...slots.Trigger?.props.style },
              'data-testid': slots.Trigger?.props['data-testid'],
              readOnly,
              ariaDisabled,
            },
            filter: {
              className: slots.Filter?.props.className,
              style: slots.Filter?.props.style,
              'data-testid': slots.Filter?.props['data-testid'],
            },
            content: {
              className: slots.Content?.props.className,
              style: slots.Content?.props.style,
              'data-testid': slots.Content?.props['data-testid'],
            },
            emptyState: {
              className: slots.EmptyState?.props.className,
              style: slots.EmptyState?.props.style,
              'data-testid': slots.EmptyState?.props['data-testid'],
            },
          }}
          onFocus={onFocus}
          onKeyDown={(event) => {
            if (!menuOpen) {
              if (event.key === 'Enter' || event.key === ' ') {
                dispatch({
                  type: SelectActions.SELECTFOCUS,
                });
              }

              return true;
            }

            // close the dropdown on Escape and focus the trigger
            if (event.key === 'Escape') {
              dispatch({
                type: SelectActions.SELECTFOCUS,
              });

              hiddenTrigger?.focus();
            }

            if (filterRef.current || clearSelectionRef.current) {
              // focus is on filter
              if (document.activeElement === filterRef.current?.inputRef) {
                // We only want to remove selected values with Backspace when the focus is not on the filter input
                if (
                  event.key === 'Backspace' &&
                  filterRef.current.inputRef?.value === ''
                ) {
                  event.preventDefault();
                  event.stopPropagation();
                }

                // focus back on the trigger on tab if there is no clear selection element or shift tab is pressed
                if (event.key === 'Tab') {
                  if (!clearSelectionRef.current || event.shiftKey) {
                    event.preventDefault();
                    event.stopPropagation();
                    hiddenTrigger?.focus();
                  } else {
                    clearSelectionRef.current && setFocusOnOption(false); // prevent setting the focus on the option if there is a clear selection button when tabbing - the clear button should be the only element focused then
                  }
                }

                // close the overlay when using a single select and selecting an option while searching in the filter
                if (event.key === 'Enter' && !props.multiple) {
                  dispatch({
                    type: SelectActions.SELECTFOCUS,
                  });
                  hiddenTrigger?.focus();
                }

                // do not select any options if the select is in a loading state
                if (event.key === 'Enter' && slots.Content?.props.loading) {
                  event.preventDefault();
                  event.stopPropagation();
                }

                // do not select any options if typing space as the first character of the filter
                if (event.key === ' ' && !filterValue) {
                  setFilterValue(' ');
                  event.preventDefault();
                  event.stopPropagation();
                }

                handleArrowKeyOptionFocus(event.key);

                return true;
              }

              // focus is on the clear selection element
              if (document.activeElement === clearSelectionRef.current) {
                setFocusOnOption(false);
                if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                  event.preventDefault();
                }

                // focus the trigger on tab (if it is not a shift tab press to focus back on the filter)
                if (
                  event.key === 'Tab' &&
                  !(event.shiftKey && filterRef.current)
                ) {
                  event.preventDefault();
                  event.stopPropagation();
                  hiddenTrigger?.focus();
                  setFocusOnOption(true);
                }

                // focus moves from the clear selection element back to the input
                if (
                  event.key === 'Tab' &&
                  event.shiftKey &&
                  filterRef.current
                ) {
                  setFocusOnOption(true);
                }

                return false;
              }

              try {
                // focus is on hidden input element
                if (
                  document.activeElement &&
                  document.getElementById(document.activeElement.id)
                ) {
                  if (event.key === 'Tab') {
                    event.preventDefault();
                    event.stopPropagation();

                    if (!event.shiftKey) {
                      // set focus on filter if available
                      if (filterRef.current) {
                        filterRef.current.inputRef?.focus();
                        return false;
                      }

                      // set focus on clearSelection if available
                      if (clearSelectionRef.current) {
                        clearSelectionRef.current.focus();
                        setFocusOnOption(false);
                        return false;
                      }
                    } else {
                      // set focus on clearSelection if available
                      if (clearSelectionRef.current) {
                        clearSelectionRef.current.focus();
                        setFocusOnOption(false);
                        return false;
                      }
                      // set focus on filter if available
                      if (filterRef.current) {
                        filterRef.current.inputRef?.focus();
                        return false;
                      }
                    }
                  }

                  handleArrowKeyOptionFocus(event.key);

                  // do not select any options if the select is in a loading state
                  if (slots.Content?.props.loading) {
                    event.preventDefault();
                    event.stopPropagation();
                  }
                }
              } catch (error) {
                // eslint-disable-next-line no-console
                console.log(
                  'Invalid query selector on the SelectV2 trigger',
                  error,
                );
              }
              return false;
            }

            try {
              // close the dropdown when tab is pressed and no filter or clear selection is available
              if (
                document.activeElement &&
                document.getElementById(document.activeElement.id)
              ) {
                if (event.key === 'Tab') {
                  dispatch({
                    type: SelectActions.SELECTFOCUS,
                  });
                } else {
                  handleArrowKeyOptionFocus(event.key);
                }
              }
            } catch (error) {
              // eslint-disable-next-line no-console
              console.log(
                'Invalid query selector on the SelectV2 trigger',
                error,
              );
            }

            setFocusOnOption(true);
          }}
        />
      </div>
    </SelectMenuContext.Provider>
  );
}

const SelectForwardRef = forwardRef(SelectV2Fwd);

/**
 * SelectV2 component that uses a fallback FormFieldMessages-context if it wasn't wrapped in a FormField.
 */
const SelectV2Root =
  withFormFieldMessageContextProviderWithGenerics(SelectForwardRef);

/**
 * The `SelectV2` component allows you to choose one or multiple
 * options from a collapsed dropdown with options.
 * @public
 */
export const SelectV2 = Object.assign(SelectV2Root, {
  Content: SelectV2Content,
  Option: SelectV2Option,
  Group: SelectV2Group,
  GroupLabel: SelectV2GroupLabel,
  Trigger: SelectV2Trigger,
  DisplayValue: SelectV2DisplayValue,
  Prefix: SelectV2Prefix,
  Suffix: SelectV2Suffix,
  CustomTrigger: SelectV2CustomTrigger,
  Filter: SelectV2Filter,
  EmptyState: SelectV2EmptyState,
});

(SelectV2 as typeof SelectV2 & { displayName: string }).displayName = 'Select';
